/**
 * Typed reading and writing of form fields.
 *
 * Every dashboard page redefined its own `read()` and `setField()` helpers,
 * with the same chains of `instanceof` copied over. Gathered here, they notably
 * guarantee that checkboxes and numeric fields are handled the same way
 * everywhere.
 */

/** An input field, whatever its type. */
export type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

export function isFormField(element: unknown): element is FormField {
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement
  );
}

/**
 * Retrieves a field by its `name` attribute.
 *
 * A radio group needs the extra branch: several controls share one name, so
 * `namedItem` hands back a `RadioNodeList` rather than an element. Without it
 * every radio group read as an empty string - silently, since an empty string
 * is a perfectly ordinary value for a text field. The option cards on the
 * Welcome page were saving nothing at all.
 */
export function field(form: HTMLFormElement, name: string): FormField | null {
  const element = form.elements.namedItem(name);
  if (isFormField(element)) return element;

  if (element instanceof RadioNodeList) {
    /*
     * A list is not always a radio group.
     *
     * `namedItem` matches on **`id` as well as `name`**, so an upgraded select
     * — which keeps the original tag's `id` on its trigger and carries the
     * value in a hidden input of the same `name` — produces a two-element list
     * with no radio in it. `.value` of such a list is the empty string, and the
     * Settings page therefore sent `language: ""` on every save: the API
     * answered "Invalid option" about a field nobody had touched.
     *
     * So: the checked radio when there is one, otherwise the single element
     * that genuinely carries this `name`. A real radio group has several and
     * falls through to `null`, exactly as before.
     */
    const checked = [...element].find((node) => node instanceof HTMLInputElement && node.checked);
    if (isFormField(checked)) return checked;

    const named = [...element].filter(
      (node) => node instanceof HTMLElement && node.getAttribute('name') === name,
    );

    if (named.length === 1 && isFormField(named[0])) return named[0];
  }

  return null;
}

/** Value of a field: boolean for a checkbox, number for a `type="number"`. */
export function readField(form: HTMLFormElement, name: string): string | number | boolean {
  const target = field(form, name);
  if (!target) return '';

  if (target instanceof HTMLInputElement) {
    if (target.type === 'checkbox') return target.checked;
    if (target.type === 'number') return target.valueAsNumber || 0;
  }
  return target.value;
}

export function readText(form: HTMLFormElement, name: string): string {
  return String(readField(form, name));
}

export function readBoolean(form: HTMLFormElement, name: string): boolean {
  return readField(form, name) === true;
}

/** Writes a value into a field, honouring its type. */
export function setField(
  form: HTMLFormElement,
  name: string,
  value: string | number | boolean,
): void {
  const target = field(form, name);
  if (!target) return;

  if (target instanceof HTMLInputElement && target.type === 'checkbox') {
    target.checked = Boolean(value);
    return;
  }
  target.value = String(value);
}

/**
 * Fills a form from an object, flattening the nested structures:
 * `{ panel: { title } }` feeds the `panel.title` field.
 */
export function fillForm(
  form: HTMLFormElement,
  values: Record<string, unknown>,
  prefix = '',
): void {
  for (const [key, value] of Object.entries(values)) {
    const name = prefix ? `${prefix}.${key}` : key;

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      fillForm(form, value as Record<string, unknown>, name);
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      setField(form, name, value);
    }
  }
}

/** Writes a value into an object from a dotted path. */
export function setPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const segments = path.split('.');
  let cursor = target;

  for (const key of segments.slice(0, -1)) {
    cursor[key] ??= {};
    cursor = cursor[key] as Record<string, unknown>;
  }

  const last = segments.at(-1);
  if (last) cursor[last] = value;
}

/**
 * Builds a nested object from a form's `name` attributes.
 * `welcome.embed.title` becomes `{ welcome: { embed: { title } } }`.
 */
export function formToObject(form: HTMLFormElement): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const element of form.elements) {
    if (!isFormField(element) || !element.name || element.disabled) continue;
    // Files are transmitted separately, in FormData.
    if (element instanceof HTMLInputElement && element.type === 'file') continue;

    setPath(result, element.name, readField(form, element.name));
  }

  return result;
}

/** Empties every file field - called after a successful save. */
export function clearFileInputs(form: HTMLFormElement): void {
  for (const element of form.elements) {
    if (element instanceof HTMLInputElement && element.type === 'file') element.value = '';
  }
}

/** Attaches a form's files to a `FormData`. */
export function appendFiles(form: HTMLFormElement, body: FormData): void {
  for (const element of form.elements) {
    if (!(element instanceof HTMLInputElement) || element.type !== 'file' || !element.name)
      continue;
    const file = element.files?.[0];
    if (file) body.set(element.name, file);
  }
}
